/**
 * Progression Service
 * API calls for XP, levels, milestones, and leaderboard operations
 */

import { apiClient as api } from './apiClient';

// ============================================
// TYPES
// ============================================

export type UserTitle = 'beginner' | 'apprentice' | 'scholar' | 'expert' | 'master' | 'legend';
export type AvatarFrame = 'none' | 'bronze' | 'silver' | 'gold' | 'platinum' | 'diamond';

export interface LevelInfo {
    level: number;
    totalXp: number;
    currentLevelXp: number;
    nextLevelXp: number;
    progressPercent: number;
    xpInCurrentLevel: number;
    xpNeededForNextLevel: number;
    activeTitle: UserTitle;
    activeFrame: AvatarFrame;
    unlockedTitles: UserTitle[];
    unlockedFrames: AvatarFrame[];
    weeklyXp: number;
}

export interface MilestoneInfo {
    xpThreshold: number;
    tokens: number;
    claimed: boolean;
    claimable: boolean;
    claimedAt?: string;
}

export interface TitleInfo {
    title: UserTitle;
    unlocked: boolean;
    active: boolean;
    metadata: {
        name: { en: string; 'zh-TW': string; 'zh-CN': string; ko: string };
        description: { en: string; 'zh-TW': string; 'zh-CN': string; ko: string };
        icon: string;
    };
}

export interface FrameInfo {
    frame: AvatarFrame;
    unlocked: boolean;
    active: boolean;
    metadata: {
        name: { en: string; 'zh-TW': string; 'zh-CN': string; ko: string };
        color: string;
        cssClass: string;
    };
}

export interface LeaderboardEntry {
    rank: number;
    userId: string;
    displayName: string | null;
    avatarUrl: string | null;
    totalPoints: number;
    questsCompleted: number;
    level: number;
    title: UserTitle;
}

export interface LeaderboardResponse {
    rankings: LeaderboardEntry[];
    weekStartDate: string;
    weekEndDate: string;
}

export interface LeaderboardPosition {
    rank: number | null;
    totalPoints: number;
    questsCompleted: number;
    totalParticipants: number;
}

export interface LeaderboardHistoryEntry {
    id: string;
    userId: string;
    courseId: string | null;
    weekEndDate: string;
    weekStartDate: string;
    finalRank: number;
    totalPoints: number;
    questsCompleted: number;
    tokensAwarded: number;
    badgeAwarded: string | null;
    displayName: string | null;
}

export interface ProgressionStats {
    level: LevelInfo;
    milestones: {
        total: number;
        claimed: number;
        claimable: number;
        nextMilestone: number | null;
    };
    leaderboard: {
        currentRank: number | null;
        currentPoints: number;
        totalParticipants: number;
    };
    badges: {
        total: number;
        recent: string[];
    };
}

// ============================================
// API FUNCTIONS
// ============================================

/**
 * Get current user's level info
 */
export async function getLevelInfo(): Promise<LevelInfo> {
    const response = await api.get<LevelInfo>('/progression/level');
    return response.data;
}

/**
 * Set active title
 */
export async function setActiveTitle(title: UserTitle): Promise<{ success: boolean; activeTitle: UserTitle }> {
    const response = await api.post<{ success: boolean; activeTitle: UserTitle }>('/progression/level/set-title', { title });
    return response.data;
}

/**
 * Set active avatar frame
 */
export async function setActiveFrame(frame: AvatarFrame): Promise<{ success: boolean; activeFrame: AvatarFrame }> {
    const response = await api.post<{ success: boolean; activeFrame: AvatarFrame }>('/progression/level/set-frame', { frame });
    return response.data;
}

/**
 * Get available titles
 */
export async function getAvailableTitles(): Promise<TitleInfo[]> {
    const response = await api.get<TitleInfo[]>('/progression/titles');
    return response.data;
}

/**
 * Get available frames
 */
export async function getAvailableFrames(): Promise<FrameInfo[]> {
    const response = await api.get<FrameInfo[]>('/progression/frames');
    return response.data;
}

/**
 * Get all milestones with status
 */
export async function getMilestones(): Promise<MilestoneInfo[]> {
    const response = await api.get<MilestoneInfo[]>('/progression/milestones');
    return response.data;
}

/**
 * Claim a milestone reward
 */
export async function claimMilestone(xpThreshold: number): Promise<{ tokens: number; newBalance: number }> {
    const response = await api.post<{ tokens: number; newBalance: number }>(`/progression/milestones/${xpThreshold}/claim`);
    return response.data;
}

/**
 * Get weekly leaderboard
 */
export async function getWeeklyLeaderboard(options?: {
    courseId?: string;
    limit?: number;
}): Promise<LeaderboardResponse> {
    const params = new URLSearchParams();
    if (options?.courseId) params.append('courseId', options.courseId);
    if (options?.limit) params.append('limit', options.limit.toString());

    const response = await api.get<LeaderboardResponse>(`/progression/leaderboard/weekly?${params.toString()}`);
    return response.data;
}

/**
 * Get user's leaderboard position
 */
export async function getLeaderboardPosition(courseId?: string): Promise<LeaderboardPosition> {
    const params = courseId ? `?courseId=${courseId}` : '';
    const response = await api.get<LeaderboardPosition>(`/progression/leaderboard/position${params}`);
    return response.data;
}

/**
 * Get user's leaderboard history
 */
export async function getLeaderboardHistory(options?: {
    limit?: number;
}): Promise<LeaderboardHistoryEntry[]> {
    const params = options?.limit ? `?limit=${options.limit}` : '';
    const response = await api.get<LeaderboardHistoryEntry[]>(`/progression/leaderboard/my-history${params}`);
    return response.data;
}

/**
 * Get comprehensive progression stats
 */
export async function getProgressionStats(): Promise<ProgressionStats> {
    const response = await api.get<ProgressionStats>('/progression/stats');
    return response.data;
}

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Get title display name based on locale
 */
export function getTitleName(title: UserTitle, locale = 'zh-TW'): string {
    const names: Record<UserTitle, Record<string, string>> = {
        beginner: { en: 'Beginner', 'zh-TW': '初學者', 'zh-CN': '初学者', ko: '초보자' },
        apprentice: { en: 'Apprentice', 'zh-TW': '學徒', 'zh-CN': '学徒', ko: '견습생' },
        scholar: { en: 'Scholar', 'zh-TW': '學者', 'zh-CN': '学者', ko: '학자' },
        expert: { en: 'Expert', 'zh-TW': '專家', 'zh-CN': '专家', ko: '전문가' },
        master: { en: 'Master', 'zh-TW': '大師', 'zh-CN': '大师', ko: '마스터' },
        legend: { en: 'Legend', 'zh-TW': '傳奇', 'zh-CN': '传奇', ko: '레전드' },
    };
    return names[title]?.[locale] || names[title]?.en || title;
}

/**
 * Get frame display name based on locale
 */
export function getFrameName(frame: AvatarFrame, locale = 'zh-TW'): string {
    const names: Record<AvatarFrame, Record<string, string>> = {
        none: { en: 'None', 'zh-TW': '無', 'zh-CN': '无', ko: '없음' },
        bronze: { en: 'Bronze', 'zh-TW': '銅框', 'zh-CN': '铜框', ko: '브론즈' },
        silver: { en: 'Silver', 'zh-TW': '銀框', 'zh-CN': '银框', ko: '실버' },
        gold: { en: 'Gold', 'zh-TW': '金框', 'zh-CN': '金框', ko: '골드' },
        platinum: { en: 'Platinum', 'zh-TW': '白金框', 'zh-CN': '白金框', ko: '플래티넘' },
        diamond: { en: 'Diamond', 'zh-TW': '鑽石框', 'zh-CN': '钻石框', ko: '다이아몬드' },
    };
    return names[frame]?.[locale] || names[frame]?.en || frame;
}

/**
 * Get level unlock info for titles
 */
export function getTitleUnlockLevel(title: UserTitle): number {
    const levels: Record<UserTitle, number> = {
        beginner: 1,
        apprentice: 5,
        scholar: 10,
        expert: 15,
        master: 20,
        legend: 25,
    };
    return levels[title] || 1;
}

/**
 * Get level unlock info for frames
 */
export function getFrameUnlockLevel(frame: AvatarFrame): number {
    const levels: Record<AvatarFrame, number> = {
        none: 1,
        bronze: 5,
        silver: 10,
        gold: 15,
        platinum: 20,
        diamond: 25,
    };
    return levels[frame] || 1;
}

/**
 * Format XP number with locale-specific thousands separator
 */
export function formatXp(xp: number, locale = 'zh-TW'): string {
    return xp.toLocaleString(locale);
}
